import React, { useRef, useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from "chart.js";
import { Box, useTheme } from "@mui/material";
import { tokens } from "../../../theme";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

function throttle(func, limit) {
    let lastFunc;
    let lastRan;
    return function (...args) {
        const context = this;
        if (!lastRan) {
            func.apply(context, args);
            lastRan = Date.now();
        } else {
            clearTimeout(lastFunc);
            lastFunc = setTimeout(function () {
                if ((Date.now() - lastRan) >= limit) {
                    func.apply(context, args);
                    lastRan = Date.now();
                }
            }, limit - (Date.now() - lastRan));
        }
    };
}


const DeltaChart = ({
  dataValues = [-50, -20, 10, 40, -10],
  labels = ["A", "B", "C", "D", "E"],
  height = 300,
  onCallback
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const chartRef = useRef(null);
    const [chartData, setChartData] = useState({ labels: [], datasets: [] });

    useEffect(() => {
        if (!chartRef.current) return;
        const chart = chartRef.current;
        const ctx = chart.ctx;

        const min = Math.min(...dataValues, 0);
        const max = Math.max(...dataValues, 0);

        // const zeroPos = (max / (max - min)) * chart.height;
        const zeroPos = (max - min !== 0) ? (max / (max - min)) * chart.height : chart.height / 2;


        // Compute the zero position in canvas coordinates
        const zeroY = chart.scales.y.getPixelForValue(0);

        // Positive area gradient (above zero)
        const gradientAbove = ctx.createLinearGradient(0, zeroY, 0, chart.scales.y.getPixelForValue(Math.max(...dataValues)));
        gradientAbove.addColorStop(1, theme.palette.mode == "dark" ? colors.blue[300] : colors.primary[300]); // near the line
        gradientAbove.addColorStop(0,  theme.palette.mode == "dark" ? colors.blue[100] : colors.primary[100]); // at zero

        // Negative area gradient (below zero)
        const gradientBelow = ctx.createLinearGradient(0, chart.scales.y.getPixelForValue(Math.min(...dataValues)), 0, zeroY);
        gradientBelow.addColorStop(1, colors.red[100]); // at zero
        gradientBelow.addColorStop(0, colors.red[300]);  // near the line

        const lineGradient = ctx.createLinearGradient(0, 0, 0, chart.height);
        lineGradient.addColorStop(0, theme.palette.mode == "dark" ? colors.blue[400] : colors.primary[400]);
        lineGradient.addColorStop(0.5, theme.palette.mode == "dark" ? colors.blue[400] : colors.primary[400]);
        lineGradient.addColorStop(0.5, colors.red[400]);
        lineGradient.addColorStop(1, colors.red[400]);

        setChartData({
            labels,
            datasets: [
                {
                label: "Delta",
                data: dataValues,
                borderColor: lineGradient,
                tension: 0.3,
                fill: { target: "origin", above: gradientAbove, below: gradientBelow },
                pointRadius: 0,
                pointHoverRadius: 6,
                pointBackgroundColor: "black",
                pointHoverBackgroundColor: "orange",
                },
            ],
        });
    }, [dataValues, labels]);
    
    useEffect(() => {
        const chart = chartRef.current;
        return () => {
            if (chart?.$destroyListeners) {
                chart.$destroyListeners();
            }
        };
    }, []);

    const options = {
        responsive: true,
        animation:false,
        maintainAspectRatio: false, // essential for custom height & full width
        interaction: { mode: "index", intersect: false },
        plugins: {
            tooltip: {
                enabled: true,
                mode: "index",
                intersect: false,

                callbacks: { 
                    label(context) {
                        const value = context.raw ?? context.parsed?.y ?? 0;
                        return `${context.label}: ${value}`;
                    }
                },
                // callbacks: { 
                //     label: (tooltipItem) => `${tooltipItem.label}: ${tooltipItem.raw}` 
                // },
            },
        },
        scales: {
            y: { 
                beginAtZero: true, 
                grid: { display: false },
                min: (() => {
                    const m = Math.max(
                        Math.abs(Math.min(...dataValues)),
                        Math.abs(Math.max(...dataValues))
                    );
                    return m === 0 ? -1 : -m;  // fallback to -1 if all zeros
                })(),

                max: (() => {
                    const m = Math.max(
                        Math.abs(Math.min(...dataValues)),
                        Math.abs(Math.max(...dataValues))
                    );
                    return m === 0 ? 1 : m;     // fallback to 1 if all zeros
                })(),
            },
            x: { grid: { display: false } },
        },
    };

    const getSecondarySetOnHover = {
        id: "getSecondarySetOnHover",
        afterEvent(chart, args) {
            const { canvas,data } = chart;
            
            if (!canvas._hoverListenersAttached) {
                canvas._hoverListenersAttached = true;

                // Throttled callback
                const throttledCallback = throttle(onCallback, 50); // call at most every 50ms

                const handleMouseMove = (e) => {
                    const points = chart.getElementsAtEventForMode(
                        e,
                        "nearest",
                        { intersect: false },
                        true
                    );
                    if (points.length) throttledCallback(points[0].index);
                };

                const handleMouseLeave = () => {
                    const lastIndex = data?.datasets[0].data.length - 1;
                    throttledCallback(lastIndex);
                };

                canvas.addEventListener("mousemove", handleMouseMove);
                canvas.addEventListener("mouseleave", handleMouseLeave);

                // Optional: clean up if chart is destroyed
                chart.$destroyListeners = () => {
                    canvas.removeEventListener("mousemove", handleMouseMove);
                    canvas.removeEventListener("mouseleave", handleMouseLeave);
                };
            }
        },
    }
    
  // Full-width wrapper div
    return (
        <Box 
            sx={{
                width:1,
                height,
            }}
        >
            <Line 
                ref={chartRef} 
                data={chartData} 
                options={options} 
                plugins={[getSecondarySetOnHover]}
            />
        </Box>
    );
};

export default DeltaChart;
